<template>
  <v-container max-width="1200" class="pa-4">
    <v-btn color="primary" @click="showModal">Add Field</v-btn>
    <v-table class="table table-striped">
      <thead class="thead-dark">
        <tr>
          <th></th>
          <th scope="col">Name</th>
          <th scope="col">Description</th>
          <th scope="col">Type</th>
          <th scope="col">Actions</th>
        </tr>
      </thead>

      <draggable
        v-model="fields"
        item-key="order"
        tag="tbody"
        :group="{ name: 'fields', pull: 'clone', put: false }"
        :animation="200"
        :ghostClass="'ghost'"
        :forceFallback="true"
        filter=".ignore-drag"
        @end="onDragEnd">
        <template #item="{ element }">
          <tr>
            <td>
              <v-icon class="cursor-move">mdi-drag</v-icon>
            </td>
            <td class="ignore-drag">{{ element.name }}</td>
            <td class="ignore-drag">{{ element.description }}</td>
            <td class="ignore-drag">{{ element.type.replace('_', '-') }}</td>
            <td class="ignore-drag">
              <v-icon @click="showModal(element)">mdi-pencil</v-icon>
              <v-icon @click="openDeleteModal(element)">mdi-trash-can-outline</v-icon>
            </td>
          </tr>
        </template>
      </draggable>
    </v-table>
  </v-container>

  <RubricAddEditFieldComponent
    :section="section"
    :year-id="yearId"
    :is-open="showAddField"
    :is-add="isAdd"
    :field="selectedField"
    :rubric-id="rubricId"
    @snack="throwSnack"
    @Rubric-field-created="handleRubricFieldSaved"
    @Rubric-field-cancled="showAddField = false"></RubricAddEditFieldComponent>
  <ConfirmDeleteComponent
    :isOpen="showDelete"
    item="Field"
    :description="selectedField?.name || ''"
    @actionConfirmed="handleRubricFieldDelete"
    @actionCanceled="showDelete = false"></ConfirmDeleteComponent>
</template>
<script setup lang="ts">
import type { SortableEvent } from 'sortablejs';
import { RubricFieldDTO, IRubricFieldDTO, IRubricSectionDTO } from '@/dtos';
import draggable from 'vuedraggable';
import { computed, ref, watch } from 'vue';
import RubricAddEditFieldComponent from './RubricAddEditFieldComponent.vue';
import { SNACKSTATUS } from '@/stores/util';
import { useSnack } from '@/helpers';
import { useRubricStore, useUpdateStatusStore } from '@/stores';
import ConfirmDeleteComponent from '../ui/ConfirmDeleteComponent.vue';

const emit = defineEmits<{
  (e: 'snack', objType: string, snackType: SNACKSTATUS, err?: string): void;
}>();
const { throwSnack } = useSnack(emit);

const props = defineProps<{ section: IRubricSectionDTO; yearId: string; rubricId: string }>();
const section = computed(() => props.section);
const yearId = computed(() => props.yearId);
const rubricId = computed(() => props.rubricId);
const fields = ref<IRubricFieldDTO[]>([]);
const statusStore = useUpdateStatusStore();
const rubricStore = useRubricStore();
const selectedField = ref<IRubricFieldDTO | undefined>(undefined);
const isAdd = ref<boolean>(false);
const showDelete = ref<boolean>(false);

watch(
  () => section.value.fields,
  (value: IRubricFieldDTO[]) => {
    fields.value = value.map((field) => new RubricFieldDTO(field));
  },
  { immediate: true },
);

const showAddField = ref<boolean>(false);

const handleRubricFieldSaved = () => {
  showAddField.value = false;
};

function onDragEnd(evt: SortableEvent) {
  if (evt.oldIndex === evt.newIndex) {
    return;
  }
  fields.value.forEach((field, index) => {
    field.order = index + 1;
  });
  rubricStore.updateFields.mutateAsync({
    rubricId: rubricId.value,
    sectionId: section.value.id,
    fields: fields.value,
    yearId: yearId.value,
  });
}
const key = computed(() => `Rubric-fields-update-${section.value.id}`);

watch(
  () => statusStore.getStatus(key.value),
  (value: SNACKSTATUS | undefined) => {
    if (!value || section.value.id == '') {
      return;
    }
    emit('snack', 'Fields', value, statusStore.getError(key.value));
  },
);

const showModal = (field?: IRubricFieldDTO) => {
  if (field && field.id) {
    isAdd.value = false;
    selectedField.value = new RubricFieldDTO(field);
  } else {
    isAdd.value = true;
    selectedField.value = undefined;
  }
  showAddField.value = true;
};

const openDeleteModal = (field: IRubricFieldDTO) => {
  selectedField.value = new RubricFieldDTO(field);
  showDelete.value = true;
};

const handleRubricFieldDelete = () => {
  if (!selectedField.value || !selectedField.value.id) {
    return;
  }
  rubricStore.removeField.mutateAsync({
    field: selectedField.value,
    yearId: yearId.value,
  });
  showDelete.value = false;
};
</script>

<!-- TODO
1. Make Pretty
-->
